// Audit Log viewer — paginated, filterable audit trail.
// Route: /audit-logs
// Tabs: Audit log (default) | Alerts (admin-only, ADR-014 Q1 S3 surface)

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { JSX, ChangeEvent, KeyboardEvent } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { AuditTablePanel, type AuditEntry } from "../components/AuditTable.js";
import { SecurityAlertsPanel } from "../components/SecurityAlertsPanel.js";
import { downloadCsv } from "../lib/csv-export.js";
import { useAuth } from "../hooks/useAuth.js";

type PageTab = "audit" | "alerts";

// Roles with access to the Alerts tab. Mirrors the API's requireAdmin guard
// at apps/api/src/middleware/roles.ts; defense in depth (the API enforces
// the gate regardless of the UI).
const ALERTS_TAB_ROLES = new Set(["agency_admin"]);

interface AuditLogResponse {
  data: AuditEntry[]; total: number; page: number; limit: number;
}

const LIMIT = 20;

const RESOURCE_TYPES = [
  { value: "", label: "All resources" },
  { value: "patient", label: "Patient" },
  { value: "service_request", label: "Service Request" },
  { value: "carer", label: "Carer" },
  { value: "medication", label: "Medication" },
];

function buildAuditUrl(page: number, resourceType: string, resourceId: string): string {
  const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
  if (resourceType) params.set("resource_type", resourceType);
  if (resourceId)   params.set("resource_id", resourceId);
  return `/audit-logs?${params.toString()}`;
}

function exportToCsv(items: AuditEntry[]): void {
  if (items.length === 0) { toast.warning("No audit entries to export."); return; }
  downloadCsv(`audit-log-${new Date().toISOString().slice(0, 10)}.csv`, [
    { header: "Timestamp",     value: (e) => e.created_at },
    { header: "Actor",         value: (e) => e.actor_id },
    { header: "Actor role",    value: (e) => e.actor_role },
    { header: "Action",        value: (e) => e.action },
    { header: "Resource type", value: (e) => e.resource_type },
    { header: "Resource ID",   value: (e) => e.resource_id },
    { header: "New value",     value: (e) => e.new_value ? JSON.stringify(e.new_value) : "" },
  ], items);
  toast.success(`Exported ${items.length} audit entr${items.length === 1 ? "y" : "ies"}.`);
}

interface FiltersBarProps {
  resourceType: string; inputValue: string;
  onResourceTypeChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onInputChange: (v: string) => void;
  onSearch: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

function FiltersBar(p: FiltersBarProps): JSX.Element {
  return (
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      <select value={p.resourceType} onChange={p.onResourceTypeChange}
        className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-navy-400">
        {RESOURCE_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
      </select>
      <div className="flex items-center gap-1.5">
        <input type="text" placeholder="Search by resource ID…" value={p.inputValue}
          onChange={(e) => p.onInputChange(e.target.value)} onKeyDown={p.onKeyDown}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-navy-400 w-52" />
        <button onClick={p.onSearch} className="text-sm bg-navy-700 text-white rounded-lg px-3 py-1.5 hover:bg-navy-800 transition-colors">Search</button>
      </div>
    </div>
  );
}

function Pager({ page, totalPages, onPrev, onNext }: { page: number; totalPages: number; onPrev: () => void; onNext: () => void }): JSX.Element {
  return (
    <div className="flex items-center gap-4 justify-end text-sm text-slate-600">
      <button onClick={onPrev} disabled={page <= 1}
        className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Prev</button>
      <span>Page {page} of {totalPages}</span>
      <button onClick={onNext} disabled={page >= totalPages}
        className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next →</button>
    </div>
  );
}

function AuditLogTab(): JSX.Element {
  const [page,         setPage]         = useState(1);
  const [resourceType, setResourceType] = useState("");
  const [inputValue,   setInputValue]   = useState("");
  const [resourceId,   setResourceId]   = useState("");

  const { data, isLoading, isError } = useQuery<AuditLogResponse>({
    queryKey: ["audit-logs", page, resourceType, resourceId],
    queryFn:  () => apiFetch<AuditLogResponse>(buildAuditUrl(page, resourceType, resourceId)),
  });

  const items      = data?.data ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const ready      = !isLoading && !isError;

  function handleSearch(): void { setResourceId(inputValue.trim()); setPage(1); }
  function handleRtChange(e: ChangeEvent<HTMLSelectElement>): void { setResourceType(e.target.value); setPage(1); }
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void { if (e.key === "Enter") handleSearch(); }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <FiltersBar
          resourceType={resourceType} inputValue={inputValue}
          onResourceTypeChange={handleRtChange}
          onInputChange={setInputValue}
          onSearch={handleSearch}
          onKeyDown={handleKeyDown}
        />
        <button onClick={() => exportToCsv(items)} disabled={!ready || items.length === 0}
          className="text-sm bg-white border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 ml-3">
          Export CSV
        </button>
      </div>
      <AuditTablePanel isLoading={isLoading} isError={isError} items={items} />
      {ready && (
        <Pager page={page} totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))} />
      )}
    </>
  );
}

interface TabBarProps { tab: PageTab; onChange: (t: PageTab) => void }

function TabBar({ tab, onChange }: TabBarProps): JSX.Element {
  const cls = (active: boolean): string =>
    `text-sm px-3 py-2 -mb-px border-b-2 transition-colors ${
      active ? "border-navy-700 text-navy-800 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"
    }`;
  return (
    <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
      <button onClick={() => onChange("audit")}  className={cls(tab === "audit")}>Audit log</button>
      <button onClick={() => onChange("alerts")} className={cls(tab === "alerts")}>Alerts</button>
    </div>
  );
}

export function AuditLogPage(): JSX.Element {
  const { user } = useAuth();
  const canSeeAlerts = ALERTS_TAB_ROLES.has(user?.role ?? "");
  const [tab, setTab] = useState<PageTab>("audit");

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-4">Audit Log</h1>
        {canSeeAlerts && <TabBar tab={tab} onChange={setTab} />}
        {tab === "audit"  && <AuditLogTab />}
        {tab === "alerts" && canSeeAlerts && <SecurityAlertsPanel />}
      </div>
    </Shell>
  );
}
